/**
 * Nova Derma — text, numeric, multi-line and search fields (PRD §10.5, DS-30…33).
 *
 * Every control here shares one border/ring language so a form never looks like
 * it was assembled from two systems. The bordered box ("shell") carries the
 * chrome and `className`; the native element inside is transparent and receives
 * the forwarded ref plus every remaining prop, so `register()` lands where a
 * form library expects it.
 */

import { forwardRef, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  ChangeEvent,
  FocusEvent,
  InputHTMLAttributes,
  KeyboardEvent,
  MutableRefObject,
  ReactNode,
  Ref,
  RefCallback,
  TextareaHTMLAttributes,
} from 'react';
import { Search, X } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { MONEY_DECIMALS, roundTo } from '@nova/shared';
import { cn } from '@/lib/utils';

type FieldSize = 'sm' | 'md';

/** DS-30 — `p-1.5` is the system field. `sm` exists for controls living inside a table row. */
const SHELL_PADDING: Record<FieldSize, string> = {
  sm: 'p-1',
  md: 'p-1.5',
};

const SHELL_TEXT: Record<FieldSize, string> = {
  sm: 'text-[11px]',
  md: 'text-xs',
};

/**
 * The bordered box. Typography set here (size, family, alignment) inherits down
 * into the native element, which is why `className` on the shell still styles
 * the text the caller sees.
 */
function fieldShell(size: FieldSize, error?: string, disabled?: boolean, readOnly?: boolean): string {
  return cn(
    // `field-shell` is a hook, not chrome: DS-54's touch sizing has to land on
    // this box rather than on the transparent element inside it, or the padding
    // below is added to a target that was already 44px.
    'field-shell flex w-full items-center gap-1.5 rounded border bg-white text-slate-800',
    // DS-32 — focus lives on the box, not the transparent input inside it.
    'focus-within:outline-none focus-within:ring-1',
    SHELL_PADDING[size],
    SHELL_TEXT[size],
    error ? 'border-red-400 focus-within:ring-red-500' : 'border-slate-300 focus-within:ring-blue-500',
    // DS-33 — a derived or locked value must read as read-only without a caption.
    (disabled || readOnly) && 'border-slate-200 bg-slate-100 text-slate-500',
    disabled && 'cursor-not-allowed',
  );
}

/** The native element is chrome-free: the shell already drew the border and the ring. */
const CONTROL_RESET =
  'field-control min-w-0 flex-1 border-0 bg-transparent p-0 text-inherit placeholder:text-slate-400 ' +
  'focus:outline-none focus:ring-0 focus-visible:ring-0 disabled:cursor-not-allowed';

/**
 * Fold Arabic-Indic and Persian digits to Latin, and drop the bidi marks an RTL
 * keyboard slips in around them — invisible on screen, fatal to a phone number
 * on its way to E.164. Punctuation the user typed is left alone.
 */
function toLatinDigits(input: string): string {
  return input
    .replace(/[٠-٩۰-۹]/g, (d) => String(d.charCodeAt(0) & 0x0f))
    .replace(/[\u200e\u200f\u2066-\u2069]/g, ''); // LRM, RLM and the isolate marks — invisible, so written escaped
}

/** A number also folds the Arabic separators, since none of them survive parsing. */
function toLatinNumerals(input: string): string {
  return toLatinDigits(input)
    .replace(/٫/g, '.') // Arabic decimal separator
    .replace(/[,٬\s]/g, ''); // thousands separators
}

/**
 * Everything a number field is allowed to hold: Latin digits, one leading sign,
 * one separator. Anything else is dropped as it is typed rather than shown and
 * rejected later — a native `type="number"` box would not have accepted it either.
 */
function toNumericText(input: string, allowSign: boolean): string {
  const folded = toLatinNumerals(input);
  const sign = allowSign && folded.startsWith('-') ? '-' : '';
  const [whole = '', ...fraction] = folded.replace(/[^\d.]/g, '').split('.');
  return fraction.length > 0 ? `${sign}${whole}.${fraction.join('')}` : `${sign}${whole}`;
}

function mergeRefs<T>(...refs: Array<Ref<T> | undefined>): RefCallback<T> {
  return (node) => {
    for (const ref of refs) {
      if (typeof ref === 'function') ref(node);
      else if (ref) (ref as MutableRefObject<T | null>).current = node;
    }
  };
}

// ---------------------------------------------------------------------------
// Input
// ---------------------------------------------------------------------------

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /** Arabic validation message. Non-empty switches the field to the error palette. */
  error?: string;
  /** Lucide glyph rendered at the start side of the field. */
  icon?: LucideIcon;
  /** Unit or currency label rendered at the end side — "ml", "JOD", "%". */
  suffix?: ReactNode;
  size?: FieldSize;
  /** Escape hatch for the native element; `className` styles the bordered box. */
  inputClassName?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    error,
    icon: Icon,
    suffix,
    size = 'md',
    className,
    inputClassName,
    disabled,
    readOnly,
    type = 'text',
    onChange,
    ...rest
  },
  ref,
) {
  // DS-31 — a native number box cannot parse "٥": the browser reports the field
  // as empty and the keystroke is lost. Run it as text and fold the digits here,
  // so an Arabic keypad types a number that survives and reads back in Latin.
  const numeric = type === 'number';
  // A phone is dialled, not measured: it keeps its "+", spaces and dashes, and
  // only the digits are folded. `type`/`inputMode` are both honoured so a field
  // already carrying the tel keypad opts in without a second prop.
  const telephone = type === 'tel' || rest.inputMode === 'tel';
  const allowSign = rest.min === undefined || Number(rest.min) < 0;

  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    if (numeric || telephone) {
      const typed = event.target.value;
      const next = numeric ? toNumericText(typed, allowSign) : toLatinDigits(typed);
      // Only write back when the fold changed something — assigning `value` on an
      // uncontrolled field parks the caret at the end.
      if (next !== typed) event.target.value = next;
    }
    onChange?.(event);
  }

  return (
    <div className={cn(fieldShell(size, error, disabled, readOnly), className)}>
      {Icon ? <Icon className="h-3.5 w-3.5 shrink-0 text-slate-400" aria-hidden /> : null}
      <input
        ref={ref}
        type={numeric ? 'text' : type}
        inputMode={numeric ? 'decimal' : undefined}
        disabled={disabled}
        readOnly={readOnly}
        aria-invalid={error ? true : undefined}
        onChange={handleChange}
        className={cn(CONTROL_RESET, inputClassName)}
        {...rest}
      />
      {suffix ? (
        <span className="flex shrink-0 items-center whitespace-nowrap text-[10px] font-medium leading-none text-slate-500">
          {suffix}
        </span>
      ) : null}
    </div>
  );
});

// ---------------------------------------------------------------------------
// NumberInput — DS-31
// ---------------------------------------------------------------------------

function parseNumeric(text: string): number | undefined {
  if (text === '' || text === '-' || text === '.' || text === '-.') return undefined;
  const parsed = Number(text);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function toText(value: number | null | undefined, decimals: number): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '';
  return String(roundTo(value, decimals));
}

function clampValue(value: number, min?: number, max?: number): number {
  if (min !== undefined && value < min) return min;
  if (max !== undefined && value > max) return max;
  return value;
}

export interface NumberInputProps
  extends Omit<
    InputHTMLAttributes<HTMLInputElement>,
    'value' | 'defaultValue' | 'onChange' | 'size' | 'type' | 'min' | 'max' | 'step'
  > {
  value?: number | null;
  /** Always a number — never a string, never `NaN`. An empty field reports `undefined`. */
  onChange?: (value: number | undefined) => void;
  /** Digits allowed after the separator. 3 = money (JOD fils), 4 = quantities. */
  decimals?: number;
  min?: number;
  max?: number;
  /** Increment applied by ArrowUp / ArrowDown. */
  step?: number;
  error?: string;
  icon?: LucideIcon;
  suffix?: ReactNode;
  size?: FieldSize;
  inputClassName?: string;
}

export const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>(function NumberInput(
  {
    value,
    onChange,
    decimals = MONEY_DECIMALS,
    min,
    max,
    step = 1,
    error,
    icon: Icon,
    suffix,
    size = 'md',
    className,
    inputClassName,
    disabled,
    readOnly,
    onFocus,
    onBlur,
    onKeyDown,
    ...rest
  },
  ref,
) {
  const [text, setText] = useState(() => toText(value, decimals));
  const [focused, setFocused] = useState(false);

  // A field driven only by `onChange` must not be wiped by a `value` that was
  // never supplied; once a real value arrives the prop becomes the source of truth
  // and a `reset()` back to undefined empties the box as it should.
  const driven = useRef(value !== undefined);
  if (value !== undefined) driven.current = true;

  // While the field has focus the user's keystrokes own the text; a controlled
  // parent re-render must not yank "12." back to "12".
  useEffect(() => {
    if (focused || !driven.current) return;
    setText(toText(value, decimals));
  }, [value, decimals, focused]);

  const allowNegative = min === undefined || min < 0;
  const pattern = useMemo(() => {
    const fraction = decimals > 0 ? `(?:\\.\\d{0,${decimals}})?` : '';
    return new RegExp(`^${allowNegative ? '-?' : ''}\\d*${fraction}$`);
  }, [decimals, allowNegative]);

  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    const next = toLatinNumerals(event.target.value);
    // Reject the keystroke outright rather than accepting it and rounding later —
    // a money field simply cannot hold a fourth decimal.
    if (!pattern.test(next)) return;
    setText(next);
    onChange?.(parseNumeric(next));
  }

  function handleFocus(event: FocusEvent<HTMLInputElement>) {
    setFocused(true);
    // Reception overtypes amounts constantly; select on the next frame so the
    // mouse-up that follows a click does not collapse the selection to a caret.
    const element = event.currentTarget;
    requestAnimationFrame(() => element.select());
    onFocus?.(event);
  }

  function handleBlur(event: FocusEvent<HTMLInputElement>) {
    setFocused(false);
    const parsed = parseNumeric(text);
    if (parsed === undefined) {
      setText('');
      onChange?.(undefined);
    } else {
      // Bounds are enforced on blur, not per keystroke — otherwise a field with
      // min 100 fights you while you type the "1" of "150".
      const settled = clampValue(roundTo(parsed, decimals), min, max);
      setText(toText(settled, decimals));
      if (settled !== parsed) onChange?.(settled);
    }
    onBlur?.(event);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if ((event.key === 'ArrowUp' || event.key === 'ArrowDown') && !readOnly && !disabled) {
      event.preventDefault();
      const base = parseNumeric(text) ?? clampValue(0, min, max);
      const next = clampValue(roundTo(base + (event.key === 'ArrowUp' ? step : -step), decimals), min, max);
      setText(toText(next, decimals));
      onChange?.(next);
    }
    onKeyDown?.(event);
  }

  return (
    // DS-51 — the field's interior runs LTR so a minus sign, separators and the
    // unit label sit where the number expects them, inside an otherwise RTL form.
    <div dir="ltr" className={cn(fieldShell(size, error, disabled, readOnly), 'text-end font-mono', className)}>
      {Icon ? <Icon className="h-3.5 w-3.5 shrink-0 text-slate-400" aria-hidden /> : null}
      <input
        ref={ref}
        type="text"
        inputMode={decimals > 0 ? 'decimal' : 'numeric'}
        autoComplete="off"
        value={text}
        disabled={disabled}
        readOnly={readOnly}
        aria-invalid={error ? true : undefined}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        className={cn(CONTROL_RESET, 'text-end', inputClassName)}
        {...rest}
      />
      {suffix ? (
        <span className="flex shrink-0 items-center whitespace-nowrap font-sans text-[10px] font-medium leading-none text-slate-500">
          {suffix}
        </span>
      ) : null}
    </div>
  );
});

// ---------------------------------------------------------------------------
// Textarea
// ---------------------------------------------------------------------------

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  error?: string;
  rows?: number;
  /** Grow with the content instead of scrolling inside a fixed box. */
  autoGrow?: boolean;
  /** Ceiling for `autoGrow`, in rows, after which the box scrolls. */
  maxRows?: number;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { error, rows = 3, autoGrow = false, maxRows = 10, className, disabled, readOnly, value, onChange, ...rest },
  ref,
) {
  const inner = useRef<HTMLTextAreaElement | null>(null);
  const setRefs = useMemo(() => mergeRefs<HTMLTextAreaElement>(ref, inner), [ref]);

  const resize = useCallback(() => {
    const element = inner.current;
    if (!element || !autoGrow) return;
    const styles = getComputedStyle(element);
    const chrome =
      parseFloat(styles.paddingTop) +
      parseFloat(styles.paddingBottom) +
      parseFloat(styles.borderTopWidth) +
      parseFloat(styles.borderBottomWidth);
    const lineHeight = parseFloat(styles.lineHeight) || 18;
    const ceiling = lineHeight * maxRows + chrome;
    element.style.height = 'auto';
    const wanted = element.scrollHeight + parseFloat(styles.borderTopWidth) + parseFloat(styles.borderBottomWidth);
    element.style.height = `${Math.min(wanted, ceiling)}px`;
    element.style.overflowY = wanted > ceiling ? 'auto' : 'hidden';
  }, [autoGrow, maxRows]);

  useLayoutEffect(resize, [resize, value]);

  function handleChange(event: ChangeEvent<HTMLTextAreaElement>) {
    onChange?.(event);
    resize();
  }

  return (
    <textarea
      ref={setRefs}
      rows={rows}
      value={value}
      disabled={disabled}
      readOnly={readOnly}
      aria-invalid={error ? true : undefined}
      onChange={handleChange}
      className={cn(
        'w-full rounded border bg-white p-1.5 text-xs leading-relaxed text-slate-800',
        'placeholder:text-slate-400 focus:outline-none focus:ring-1',
        autoGrow ? 'resize-none' : 'resize-y',
        error ? 'border-red-400 focus:ring-red-500' : 'border-slate-300 focus:ring-blue-500',
        (disabled || readOnly) && 'border-slate-200 bg-slate-100 text-slate-500',
        disabled && 'cursor-not-allowed',
        className,
      )}
      {...rest}
    />
  );
});

// ---------------------------------------------------------------------------
// SearchInput
// ---------------------------------------------------------------------------

export interface SearchInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'defaultValue' | 'onChange' | 'size' | 'type'> {
  value?: string;
  /** Fires `debounceMs` after the last keystroke; the visible value updates instantly. */
  onChange: (value: string) => void;
  debounceMs?: number;
  error?: string;
  size?: FieldSize;
  inputClassName?: string;
  /** Accessible name for the clear button. */
  clearLabel?: string;
}

export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(function SearchInput(
  {
    value,
    onChange,
    debounceMs = 300,
    placeholder: placeholderProp,
    clearLabel: clearLabelProp,
    onKeyDown,
    ...rest
  },
  ref,
) {
  const { t } = useTranslation('common');
  const placeholder = placeholderProp ?? t('ui.search.placeholder');
  const clearLabel = clearLabelProp ?? t('ui.search.clear');

  const inner = useRef<HTMLInputElement | null>(null);
  const setRefs = useMemo(() => mergeRefs<HTMLInputElement>(ref, inner), [ref]);

  const [text, setText] = useState(value ?? '');
  /** Last value handed to the parent — lets us tell an echo apart from a real reset. */
  const emitted = useRef(value ?? '');
  const timer = useRef<number | undefined>(undefined);

  // The debounced call must reach the newest handler, not the one captured on
  // the keystroke that scheduled it.
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  });

  useEffect(() => () => window.clearTimeout(timer.current), []);

  // A parent clearing its filters must be reflected in the box.
  useEffect(() => {
    if (value === undefined || value === emitted.current) return;
    window.clearTimeout(timer.current);
    emitted.current = value;
    setText(value);
  }, [value]);

  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    const next = event.target.value;
    setText(next);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      emitted.current = next;
      onChangeRef.current(next);
    }, debounceMs);
  }

  const clear = useCallback(() => {
    window.clearTimeout(timer.current);
    setText('');
    emitted.current = '';
    onChangeRef.current(''); // clearing is deliberate — never debounced
    inner.current?.focus();
  }, []);

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    // Escape empties the box; it only stops there so a dialog still closes on an
    // already-empty search.
    if (event.key === 'Escape' && text) {
      event.stopPropagation();
      clear();
    }
    onKeyDown?.(event);
  }

  return (
    <Input
      {...rest}
      ref={setRefs}
      icon={Search}
      placeholder={placeholder}
      value={text}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      autoComplete="off"
      suffix={
        text ? (
          <button
            type="button"
            onClick={clear}
            aria-label={clearLabel}
            title={clearLabel}
            // DS-54 — exempt from the tablet 44px rule; it lives inside a field.
            className="no-touch-size rounded p-0.5 text-slate-400 transition-colors hover:bg-slate-200 hover:text-slate-700"
          >
            <X className="h-3 w-3" />
          </button>
        ) : null
      }
    />
  );
});
